import {ProvisionTransactionEvent} from './provision-transaction-event.enum';
import {provisionEnumLabelFallback} from './provision-enum-label-fallback';

/**
 * German presentation companion of {@link ProvisionTransactionEvent} — the SINGLE place in the cp
 * frontend that NAMES a booking provenance, following the pattern of `provision-run-status-label.ts`.
 * The labels mirror the backend `@UiEnumValueInfo(uiLabel = …)`.
 *
 * ── WHY THIS MAP LEFT THE COMPONENT
 *
 * These nine labels used to sit in `cp-provision-agent-account.component.ts` as an inline `switch`
 * with `default: return ''`. {@link ProvisionTransactionEvent} explicitly provides for new constants
 * being APPENDED — that is a routine backend change — and every appended one would have rendered the
 * "Ereignis" column of the account statement as an EMPTY CELL: no compile error, no warning, just a
 * blank on the screen that decides payouts. The map now falls through to
 * {@link provisionEnumLabelFallback}, which keeps an unmapped constant visible instead.
 *
 * ── THE THREE GROUPS (02-datenmodell.md §2.3)
 *
 * Events 0-5 are the engine's reconcile events (mandatory K-11/K-13 mapping) and always come in
 * pairs of booking and reversal — the labels keep that pairing readable side by side. PAYOUT(6) is
 * resolved via `ProvisionType.payoutTransactionType`. MANUAL_ADJUSTMENT(7) and RESERVE_TRANSFER(8)
 * are the provenance of manual bookings and never count towards the engine's reconcile actual.
 *
 * ── THE SECOND CONSUMER
 *
 * `provision-simulation-technical-label.ts` re-keys this map by CONSTANT NAME for the simulation
 * screen instead of typing the nine captions out a second time. It had its own copy once; a wording
 * change on one side would have left the account statement and the simulation protocol naming the
 * same booking differently, with nothing to report it. Change a caption HERE and both follow.
 */
export const CP_TRANSACTION_EVENT_LABELS: Readonly<Record<ProvisionTransactionEvent, string>> = {
    [ProvisionTransactionEvent.PROVISION_BOOKING]: 'Provisionsbuchung',
    [ProvisionTransactionEvent.PROVISION_BOOKING_REVERSAL]: 'Provisionsbuchung Storno',
    [ProvisionTransactionEvent.RESERVE_BOOKING]: 'Rücklagenbuchung',
    [ProvisionTransactionEvent.RESERVE_BOOKING_REVERSAL]: 'Rücklagenbuchung Storno',
    [ProvisionTransactionEvent.CANCELLATION_CHARGE]: 'Storno-Rückbelastung',
    [ProvisionTransactionEvent.CANCELLATION_CHARGE_REVERSAL]: 'Storno-Wiedergutschrift',
    [ProvisionTransactionEvent.PAYOUT]: 'Auszahlung',
    [ProvisionTransactionEvent.MANUAL_ADJUSTMENT]: 'Manuelle Korrektur',
    [ProvisionTransactionEvent.RESERVE_TRANSFER]: 'Rücklagen-Umbuchung'
};

/** German label of a booking provenance; an unmapped constant stays VISIBLE, never blank. */
export function getProvisionTransactionEventLabel(event: ProvisionTransactionEvent): string {
    return CP_TRANSACTION_EVENT_LABELS[event]
        ?? provisionEnumLabelFallback(ProvisionTransactionEvent, event);
}
